import type { AuthRole } from "@/types/auth";

/**
 * Central role-capability predicates.
 *
 * The backend enforces role tuples server-side via `require_role(...)` (403
 * `role_forbidden`); the SPA mirrors the FEW fences that remain so
 * role-blocked controls are HIDDEN rather than failing reactively — the
 * house pattern per `docs/product/experience-and-ui.md` and the
 * `MembersBilling.tsx` spec comment ("MUST NOT render at all (not just
 * disabled)"). These predicates are the one place to keep tuple changes in
 * lockstep with `backend/app/auth/dependencies.py` consumers.
 *
 * Tuple authority: `docs/product/technical-architecture.md` § Workspace &
 * Multi-Seat Model. Since PO sign-off 2026-09-01 (verbatim: "Every role
 * incl. Viewer gets writes" — ENG-EXPERIMENT-SIGNAL-ROLE-FENCE-RETIREMENT)
 * every experiment/signal route admits all four roles, so the only fences
 * the SPA still mirrors are billing (owner + billing) and delete-app
 * (owner-only).
 */

/** Normalize the possibly-absent session role to the weakest role. */
export function roleOf(role: string | null | undefined): AuthRole {
  return role === "owner" || role === "member" || role === "billing"
    ? role
    : "viewer";
}

/**
 * Mirrors `require_role("owner", "billing")` — plan changes, payment
 * method, checkout/portal sessions (technical-architecture.md:1194).
 */
export function canManageBilling(role: string | null | undefined): boolean {
  const r = roleOf(role);
  return r === "owner" || r === "billing";
}

/**
 * Mirrors `require_role("owner")` on `DELETE /api/v1/apps/{app_id}` — PO
 * sign-off 2026-09-01, verbatim: "owner-only — deletion is
 * workspace-destructive, not billing work" (was owner + billing).
 */
export function canDeleteApp(role: string | null | undefined): boolean {
  return roleOf(role) === "owner";
}
